/*
 * WF Gift Threshold — storefront progress bar (Route B, docs/gift-threshold.md).
 *
 * DISPLAY ONLY. This module never writes to the cart — no /cart/add.js, no
 * /cart/change.js, no /cart/update.js, no attributes. Entitlement is derived
 * and enforced server-side by the orders/create webhook; this bar is the
 * customer-facing promise of the same computation.
 *
 * Config is the `gift_config` section of the delivery public config
 * (planners.dand3.com /api/delivery/{store}/public), cached in sessionStorage
 * for 60s — same idiom as wf-product-labels.js. `gift_config: null` (campaign
 * disabled, or config failed to resolve) means: render nothing, do nothing.
 *
 * Boot is lazy: mounts ([data-wf-gift-progress]) are watched with an
 * IntersectionObserver, so pages where the cart drawer stays closed never pay
 * for the config or cart fetch. Re-evaluation binds a MutationObserver to each
 * mount's section wrapper — Shopify section re-renders replace the section's
 * inner DOM (the message-card ghost-slot lesson), so the wrapper, which
 * survives, is what we observe. Alpine is never assumed (it boots inside rAF
 * and not at all in backgrounded tabs).
 */
(function () {
  'use strict';

  if (window.WF_CART_GIFT_THRESHOLD) return;

  var API_BASE = 'https://planners.dand3.com';
  var CACHE_TTL_MS = 60000;
  var MOUNT_SELECTOR = '[data-wf-gift-progress]';
  // `?wf_gift_preview=1` (sticky for the tab via sessionStorage; `=0` clears)
  // asks the worker for the configured-but-not-live campaign so the bar can be
  // checked on a preview theme without switching the campaign on. Display
  // only — nothing is awarded in preview.
  var PREVIEW_PARAM = 'wf_gift_preview';
  var PREVIEW_STORAGE_KEY = 'wf_gift_preview';

  function previewMode() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      // Chat clients glue trailing prose onto pasted links ("…=1 — …"), so
      // only the leading character decides.
      var flag = String(params.get(PREVIEW_PARAM) || '').trim().charAt(0);
      if (flag === '1') sessionStorage.setItem(PREVIEW_STORAGE_KEY, '1');
      if (flag === '0') sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
      return sessionStorage.getItem(PREVIEW_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function storeKey() {
    return String(
      window.WF_LD_STORE_KEY ||
      (window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.storeKey) ||
      'WF'
    );
  }

  function isB2B() {
    return Boolean(window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.b2b === true);
  }

  // --- pure helpers (exposed for tests via __test) --------------------------

  function propValue(properties, name) {
    if (!properties || typeof properties !== 'object') return '';
    var raw = properties[name];
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
  }

  function isFeeCarrierLine(properties) {
    return (
      propValue(properties, '_delivery_fee') === 'true' ||
      propValue(properties, '_express') === 'true' ||
      propValue(properties, '_wf_digital_card_fee') === 'yes'
    );
  }

  // Pre-discount merchandise subtotal in cents. Mirrors the worker's
  // computeEligibleSubtotalCents (lib/giftThreshold.ts) — the two must agree
  // or the banner promises what the webhook won't tag.
  function computeEligibleSubtotalCents(items, excludedProductIds, excludedVariantIds, includedProductIds) {
    var excludedProducts = {};
    var excludedVariants = {};
    (excludedProductIds || []).forEach(function (id) { excludedProducts[String(id)] = true; });
    (excludedVariantIds || []).forEach(function (id) { excludedVariants[String(id)] = true; });
    // Qualifying collections: when set, only these products count.
    var includedProducts = null;
    if (Array.isArray(includedProductIds)) {
      includedProducts = {};
      includedProductIds.forEach(function (id) { includedProducts[String(id)] = true; });
    }
    var total = 0;
    (items || []).forEach(function (item) {
      if (!item) return;
      if (item.gift_card === true) return;
      if (isFeeCarrierLine(item.properties)) return;
      if (excludedProducts[String(item.product_id || '')]) return;
      if (excludedVariants[String(item.variant_id || '')]) return;
      if (includedProducts && !includedProducts[String(item.product_id || '')]) return;
      var lineCents = Number(item.original_line_price);
      if (!isFinite(lineCents) || lineCents <= 0) {
        var unit = Number(item.original_price);
        var qty = Number(item.quantity);
        lineCents = (isFinite(unit) && isFinite(qty)) ? unit * qty : 0;
      }
      if (lineCents > 0) total += Math.round(lineCents);
    });
    return total;
  }

  function pickTier(tiers, cents) {
    var best = null;
    (tiers || []).forEach(function (tier) {
      if (cents >= tier.thresholdCents && (!best || tier.thresholdCents > best.thresholdCents)) {
        best = tier;
      }
    });
    return best;
  }

  function nextTier(tiers, cents) {
    var next = null;
    (tiers || []).forEach(function (tier) {
      if (cents < tier.thresholdCents && (!next || tier.thresholdCents < next.thresholdCents)) {
        next = tier;
      }
    });
    return next;
  }

  function formatMoneyCents(cents) {
    var dollars = cents / 100;
    var rounded = Math.round(dollars * 100) / 100;
    var isWhole = Math.abs(rounded - Math.round(rounded)) < 0.005;
    return 'S$' + (isWhole ? String(Math.round(rounded)) : rounded.toFixed(2));
  }

  function fillTemplate(template, tier, remainingCents) {
    return String(template || '')
      .replace(/\{remaining\}/g, formatMoneyCents(remainingCents))
      .replace(/\{label\}/g, tier ? String(tier.label || '') : '');
  }

  // View model: what the bar should show for a given eligible subtotal.
  function buildViewModel(config, eligibleCents) {
    var met = pickTier(config.tiers, eligibleCents);
    var upcoming = nextTier(config.tiers, eligibleCents);
    var lines = [];
    if (met) lines.push({ kind: 'unlocked', text: fillTemplate(config.progress.copyAt, met, 0) });
    if (upcoming) {
      lines.push({
        kind: 'progress',
        text: fillTemplate(config.progress.copyBelow, upcoming, upcoming.thresholdCents - eligibleCents),
      });
    }
    var barTarget = upcoming || met;
    var percent = 0;
    if (barTarget) {
      percent = Math.max(0, Math.min(100, Math.round((eligibleCents / barTarget.thresholdCents) * 100)));
    }
    // Thumbnail of the gift the customer is working toward (or has unlocked).
    var product = barTarget && barTarget.product ? barTarget.product : null;
    var imageUrl = product && product.imageUrl ? String(product.imageUrl) : '';
    return { lines: lines, percent: percent, met: met, upcoming: upcoming, imageUrl: imageUrl };
  }

  // --- config + cart fetch --------------------------------------------------

  function loadGiftConfig() {
    var key = storeKey();
    var preview = previewMode();
    var cacheKey = 'wf_gift_cfg_' + key + (preview ? '_preview' : '');
    try {
      var hit = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (hit && hit.t && (Date.now() - hit.t) < CACHE_TTL_MS) {
        return Promise.resolve(hit.gift || null);
      }
    } catch (e) { /* ignore */ }
    var url = API_BASE + '/api/delivery/' + encodeURIComponent(key) + '/public' + (preview ? '?giftPreview=1' : '');
    return fetch(url, {
      credentials: 'omit',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (config) {
        var gift = (config && config.gift_config) || null;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), gift: gift }));
        } catch (e) { /* ignore */ }
        return gift;
      })
      .catch(function () { return null; });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // --- rendering ------------------------------------------------------------

  function renderInto(mount, model) {
    mount.textContent = '';
    if (!model || !model.lines.length) {
      mount.hidden = true;
      return;
    }
    var box = document.createElement('div');
    box.className = 'wf-gift-progress__box';
    if (model.imageUrl && /^https:\/\//.test(model.imageUrl)) {
      var img = document.createElement('img');
      img.className = 'wf-gift-progress__thumb';
      img.src = model.imageUrl;
      img.alt = '';
      img.loading = 'lazy';
      box.appendChild(img);
      box.className += ' wf-gift-progress__box--with-thumb';
    }
    var textWrap = document.createElement('div');
    textWrap.className = 'wf-gift-progress__body';
    if (state.config && state.config.preview === true) {
      var pill = document.createElement('span');
      pill.className = 'wf-gift-progress__pill';
      pill.textContent = 'Preview — not live';
      textWrap.appendChild(pill);
    }
    model.lines.forEach(function (line) {
      var p = document.createElement('p');
      p.className = 'wf-gift-progress__text wf-gift-progress__text--' + line.kind;
      p.textContent = line.text;
      textWrap.appendChild(p);
    });
    if (model.upcoming) {
      var track = document.createElement('div');
      track.className = 'wf-gift-progress__track';
      var fill = document.createElement('div');
      fill.className = 'wf-gift-progress__fill';
      fill.style.width = model.percent + '%';
      track.appendChild(fill);
      textWrap.appendChild(track);
    }
    box.appendChild(textWrap);
    mount.appendChild(box);
    mount.hidden = false;
  }

  // --- runtime --------------------------------------------------------------

  var state = {
    booted: false,
    config: null,
    refreshTimer: null,
    refreshing: false,
  };

  function mounts() {
    var all = [];
    var nodes = document.querySelectorAll(MOUNT_SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) all.push(nodes[i]);
    return all;
  }

  function visibleMounts(config) {
    return mounts().filter(function (mount) {
      if (!config.progress.showOnDrawer && mount.getAttribute('data-context') === 'drawer') return false;
      return true;
    });
  }

  function refresh() {
    if (!state.config || state.refreshing) return;
    state.refreshing = true;
    fetchCart().then(function (cart) {
      state.refreshing = false;
      var config = state.config;
      var targets = visibleMounts(config);
      if (!targets.length) return;
      if (!cart || String(cart.currency || '').toUpperCase() !== config.currency) {
        targets.forEach(function (mount) { renderInto(mount, null); });
        return;
      }
      var eligible = computeEligibleSubtotalCents(
        cart.items,
        config.excludedProductIds,
        config.excludedVariantIds,
        config.includedProductIds
      );
      // No merchandise yet → no nudge (an empty-cart "spend S$130" is noise).
      var model = eligible > 0 ? buildViewModel(config, eligible) : null;
      targets.forEach(function (mount) { renderInto(mount, model); });
    });
  }

  function scheduleRefresh() {
    if (state.refreshTimer) clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(function () {
      state.refreshTimer = null;
      refresh();
    }, 400);
  }

  // A mutation inside one of our own mounts is our own render — never a reason
  // to refresh. Without this filter, render → observe → refresh → render loops
  // forever, fetching /cart.js every few hundred ms until Shopify throttles it.
  function isOwnMutation(record) {
    var target = record && record.target;
    if (!target || target.nodeType !== 1) return false;
    return Boolean(target.closest && target.closest(MOUNT_SELECTOR));
  }

  function onSectionMutation(records) {
    for (var i = 0; i < records.length; i += 1) {
      if (isOwnMutation(records[i])) continue;
      scheduleRefresh();
      return;
    }
  }

  function observeSectionRerenders() {
    var observed = [];
    mounts().forEach(function (mount) {
      var wrapper = mount.closest('.shopify-section') || mount.parentElement || document.body;
      if (observed.indexOf(wrapper) !== -1) return;
      observed.push(wrapper);
      try {
        new MutationObserver(onSectionMutation).observe(wrapper, { childList: true, subtree: true });
      } catch (e) { /* ignore */ }
    });
  }

  function boot() {
    if (state.booted) return;
    state.booted = true;
    loadGiftConfig().then(function (gift) {
      // Fail open: no config, inactive window, disabled progress, or B2B
      // buyer → render nothing, touch nothing.
      if (!gift || gift.active !== true || !gift.progress || gift.progress.enabled !== true) return;
      if (gift.excludeB2B && isB2B()) return;
      if (!Array.isArray(gift.tiers) || !gift.tiers.length) return;
      state.config = gift;
      observeSectionRerenders();
      refresh();
    });
  }

  // Mounts can appear AFTER this script runs (Alpine teleports/renders parts of
  // the cart after its rAF boot, and Shopify section renders replace them), so
  // arming is incremental: every mount seen so far is observed, and the DOM is
  // watched for new ones until the first boot — bounded so an unused page never
  // keeps an observer alive.
  var ARM_WATCH_MS = 30000;
  var armedNodes = [];
  var io = null;

  // Geometry check for a container that is already on screen: boot at once
  // rather than waiting on IntersectionObserver, whose callbacks ride on
  // rendering frames and can be delayed or withheld (throttled tabs, scaled
  // emulation). IO still covers containers that become visible later (drawer).
  function isOnScreen(el) {
    if (!el) return false;
    if (typeof el.checkVisibility === 'function' && !el.checkVisibility()) return false;
    var r = el.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
  }

  function armVisibleMounts() {
    var nodes = mounts();
    if (!nodes.length) return false;
    for (var i = 0; i < nodes.length; i += 1) {
      if (isOnScreen(nodes[i].parentElement || nodes[i])) {
        boot();
        return true;
      }
    }
    if (typeof IntersectionObserver !== 'function') {
      boot();
      return true;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            boot();
            return;
          }
        }
      });
    }
    nodes.forEach(function (node) {
      // Observe the mount's container, not the (empty, possibly hidden) mount
      // itself: a display:none element never intersects, which would wedge
      // the boot forever. The container is visible on the cart page and
      // becomes visible when the drawer opens — exactly the moments we want.
      var target = node.parentElement || node;
      if (armedNodes.indexOf(target) !== -1) return;
      armedNodes.push(target);
      io.observe(target);
    });
    return true;
  }

  function armLazyBoot() {
    armVisibleMounts();
    if (state.booted || typeof MutationObserver !== 'function') return;
    var watcher = new MutationObserver(function () {
      if (state.booted) {
        watcher.disconnect();
        return;
      }
      armVisibleMounts();
    });
    try {
      watcher.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { watcher.disconnect(); }, ARM_WATCH_MS);
    } catch (e) { /* ignore */ }
  }

  window.WF_CART_GIFT_THRESHOLD = {
    refresh: refresh,
    __test: {
      computeEligibleSubtotalCents: computeEligibleSubtotalCents,
      pickTier: pickTier,
      nextTier: nextTier,
      formatMoneyCents: formatMoneyCents,
      fillTemplate: fillTemplate,
      buildViewModel: buildViewModel,
      isFeeCarrierLine: isFeeCarrierLine,
    },
  };

  // Capture the preview flag at load, not only at boot: an empty cart has no
  // mount (nothing boots), yet a tester who lands there, adds items and returns
  // to /cart without the param must still be in preview mode.
  previewMode();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', armLazyBoot);
  } else {
    armLazyBoot();
  }
})();
